import { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import { GET_USER_DATA } from '../../../../../config/apiPaths';
import { getUrlParams, getUrls, processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';

interface AskTheAIContainerState {
    askTheAIData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

const useAskTheAIContainer = (state: AskTheAIContainerState = {}) => {
    console.log('AskTheAIContainer');
    const { api } = getUrls();

    const [askTheAIData, setAskTheAIData] = useState<any>();

    const { t: tCommon } = useTranslation('common');

    const { authMode } = useContainer(AdminAuthContainer);

    const search = new URLSearchParams(window.location.search);
    const askTheAIUuid = search.get('askTheAI_uuid') || window.localStorage.getItem('askTheAI_uuid');

    const [getAskTheAIStatus, setGetAskTheAIStatus] = useState<LoadingStatus>('INITIAL');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { askTheAIId: askTheAIIdV1 } = useParams<{ askTheAIId?: string }>();
    const { askTheAI_id: askTheAIIdV2 } = getUrlParams();
    const askTheAIId = askTheAIIdV1 || askTheAIIdV2 || '';

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getAskTheAIDataCall = useCallback(async () => {
        const { data } = await axios.get(
            processApiPath(GET_USER_DATA, undefined, {
                askTheAI_uuid: askTheAIUuid!,
                askTheAI_id: askTheAIId,
                auth_mode: authMode
            })
        );

        if (data?.success === false) {
            throw Error(data?.error);
        }
        return data;
    }, [askTheAIUuid, askTheAIId, authMode]);

    const getAskTheAI = useCallback(async () => {
        setGetAskTheAIStatus('LOADING');

        try {
            const data = state.askTheAIData ?? (await getAskTheAIDataCall());

            setAskTheAIData(data);
            setGetAskTheAIStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetAskTheAIStatus('ERROR');
        }
    }, [state.askTheAIData, getAskTheAIDataCall, returnErrorMessage]);

    useEffect(() => {
        if (askTheAIId && askTheAIUuid) {
            getAskTheAI();
        }
    }, [askTheAIId, getAskTheAI, askTheAIUuid]);

    return {
        askTheAIId,
        api,
        askTheAIData,
        getAskTheAIStatus,
        errorMessage
    };
};

const AskTheAIContainer = createContainer(useAskTheAIContainer);

export default AskTheAIContainer;
